import {defineAbilityConfig} from '../system/defineAbilityConfig';
import {ONLY_ON_PRIMARY_LOCALE} from './localization';
import {WHEN_NOT_IN_OPTIMIZE_MODE} from './optimization';
import {ENTITLEMENT_KEYS} from '@packages/systems/billing/common';
import type {DesignerAbilityConfig} from '../system/AbilityConfigTypes';

export const canDEAccessCanvasDefinition = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
    },
  },
] as const);

export const canDEDesignDefinition = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    localization: ONLY_ON_PRIMARY_LOCALE,
    branches: {
      main: true,
      branch: true,
    },
    workflows: {
      canvas: true,
    },
    siteModes: {design: true},
  },
] as const);

/**
 * Designer Extension ability for inserting component instances onto the canvas.
 *
 * Intentionally broader than `canDEDesign`: permits Campaign Build mode so that
 * apps (e.g. HubSpot) can drop a component instance into a Page Slot while a
 * marketer is building a campaign page. Raw element inserts still require
 * `canDEDesign` (Design mode only) — the host picks between the two abilities
 * per-call based on payload. Page Slot target validation is enforced in the
 * reducer (not at the ability layer).
 *
 * Page Build mode is intentionally excluded from this spike.
 */
export const canDEInsertComponentInstanceDefinition = defineAbilityConfig([
  // Design mode — mirrors canDEDesign's conditions so existing behavior is unchanged.
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    localization: ONLY_ON_PRIMARY_LOCALE,
    branches: {
      main: true,
      branch: true,
    },
    workflows: {
      canvas: true,
    },
    siteModes: {design: true},
  },
  // Campaign Build mode — allow DE-driven component instance inserts.
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    localization: ONLY_ON_PRIMARY_LOCALE,
    workflows: {
      canvas: true,
    },
    abilities: {
      isInCampaignBuildMode: true,
      isNotEditingCodeComponent: true,
    },
  },
] as const);

/**
 * Authorizes existing component-definition mutation methods in Design mode and
 * Campaign Build mode. This is intentionally separate from component-instance
 * insertion so a future expansion of insertion permissions cannot silently
 * expand definition mutation. The ability establishes mode-level eligibility;
 * Designer Extensions still validates the exact selected component and
 * operation before changing state.
 */
export const canDEModifySelectedCampaignComponentDefinitionDefinition =
  defineAbilityConfig<DesignerAbilityConfig[]>([
    {
      userTypes: {
        authorizedInDesigner: true,
      },
      localization: ONLY_ON_PRIMARY_LOCALE,
      branches: {
        main: true,
        branch: true,
      },
      workflows: {
        canvas: true,
      },
      siteModes: {design: true},
    },
    {
      userTypes: {
        authorizedInDesigner: true,
      },
      localization: ONLY_ON_PRIMARY_LOCALE,
      workflows: {
        canvas: true,
      },
      abilities: {
        isInCampaignBuildMode: true,
        isNotEditingCodeComponent: true,
      },
    },
  ]);

export const canDEEditDefinition = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    workflows: {
      canvas: true,
    },
    siteModes: {design: true, edit: true},
  },
  {
    abilities: {isInPageBuildMode: true},
    siteModes: {design: true, edit: true},
  },
] as const);

export const canDESetInstancePropsDefinition = defineAbilityConfig([
  {
    abilities: {canDEDesign: true},
  },
  {
    abilities: {
      canDEEdit: true,
      canEditContent: true,
      isInPageBuildMode: true,
    },
    localization: ONLY_ON_PRIMARY_LOCALE,
  },
] as const);

export const canDEModifyImageElementsDefinition = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    abilities: {canModifyImageElements: true},
    workflows: {
      canvas: true,
    },
    siteModes: {design: true, edit: true},
  },
] as const);

export const canDEWriteCodeSettingsDefinition = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    workflows: {
      canvas: true,
    },
    siteModes: {design: true},
    entitlements: [ENTITLEMENT_KEYS.ALLOW_CUSTOM_CODE],
  },
] as const);

export const canDECreateComponentsDefinition = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    localization: ONLY_ON_PRIMARY_LOCALE,
    workflows: {canvas: true},
    permission: ['create', 'siteSymbol'],
  },
  // Campaign Build mode — allow DE-driven component registration so apps
  // can define new components while a marketer is building a campaign page.
  // No `permission: ['create', 'siteSymbol']` here: marketers in Build mode
  // don't carry that site-level permission, and we rely on the installed-app
  // + Build-mode envelope for the security story, consistent with
  // `canDEInsertComponentInstance`.
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    localization: ONLY_ON_PRIMARY_LOCALE,
    workflows: {canvas: true},
    abilities: {
      isInCampaignBuildMode: true,
      isNotEditingCodeComponent: true,
    },
  },
] as const);

export const canDEModifyComponentsDefinition = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    workflows: {canvas: true},
    permission: ['modify', 'siteSymbol'],
    siteModes: {design: true},
  },
] as const);

export const canDECreateStyleBlocksDefinition = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    siteModes: {design: true},
    workflows: {canvas: true},
    permission: ['create', 'siteStyleBlock'],
    localization: ONLY_ON_PRIMARY_LOCALE,
  },
  // Campaign Build mode — allow DE-driven style block creation so apps
  // can define new styles while a marketer is building a campaign page.
  // No `permission: ['create', 'siteStyleBlock']` here: marketers in Build
  // mode don't carry that site-level permission, and we rely on the
  // installed-app + Build-mode envelope for the security story, consistent
  // with `canDECreateComponents` and `canDEInsertComponentInstance`.
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    localization: ONLY_ON_PRIMARY_LOCALE,
    workflows: {canvas: true},
    abilities: {
      isInCampaignBuildMode: true,
      isNotEditingCodeComponent: true,
    },
  },
] as const);

export const canDEModifyStyleBlocksDefinition = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    siteModes: {design: true},
    workflows: {canvas: true},
    permission: ['modify', 'siteStyleBlock'],
  },
  // Campaign Build mode — allow DE-driven style block modification so apps
  // can update styles while a marketer is building a campaign page. See
  // `canDECreateStyleBlocks` for the rationale on dropping the site-level
  // permission gate in Build mode.
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    workflows: {canvas: true},
    abilities: {
      isInCampaignBuildMode: true,
      isNotEditingCodeComponent: true,
    },
  },
] as const);

export const canDEAccessAssetsDefinition = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
    },
  },
] as const);

export const canDEManageAssetsDefinition = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    permission: ['manage', 'siteFile'],
  },
] as const);

export const canDECreatePageDefinition = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    permission: ['create', 'sitePage'],
    siteModes: {design: true},
    entitlements: [ENTITLEMENT_KEYS.MAX_PAGES],
  },
] as const);

export const canDEReadPageSettingsDefinition = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    permission: ['read', 'sitePageSettings'],
    siteModes: {design: true, edit: true},
  },
] as const);

export const canDEManagePageSettingsDefinition = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    siteModes: {design: true, edit: true},
    permission: ['manage', 'sitePageSettings'],
  },
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    siteModes: {edit: true},
    permission: ['manage', 'sitePageSettings'],
  },
] as const);

export const canDEReadVariablesDefinition = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
    },
  },
] as const);

export const canDEReadInteractionsDefinition = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    branches: {
      main: true,
    },
  },
] as const);

/**
 * Mirrors the native IX3 panel (`canAccessInteractionsPanel` in
 * `./interactions.ts`). The IX3 write responders gate on this selector alone, so
 * every constraint must be restated here; an omitted dimension is unconstrained.
 *
 * `ONLY_ON_PRIMARY_LOCALE` is deliberately omitted: DSYS-146 and DSYS-753 require
 * IX3 editing on secondary locales because interactions apply globally. Do not add
 * it to "match" the panel.
 */
export const canDEModifyInteractionsDefinition = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    isEditingCodeComponent: false,
    optimization: WHEN_NOT_IN_OPTIMIZE_MODE,
    siteModes: {design: true},
    workflows: {canvas: true},
    branches: {
      main: true,
    },
    // Interim: same permission `canDEModifyVariables` uses, so this changes no
    // access. `SITE.MANAGE_INTERACTIONS` replaces it in DSYS-7209.
    permission: ['modify', 'siteStyleBlock'],
  },
] as const);

export const canDEModifyVariablesDefinition = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    siteModes: {design: true},
    workflows: {canvas: true},
    branches: {
      main: true,
      branch: true,
    },
    permission: ['modify', 'siteStyleBlock'],
  },
] as const);
